import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_chevron_bold_left, QIcon_close_bold } from '@repo/icons/index'
import classNames from 'classnames'
import React, { Fragment, ReactElement, useEffect, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import Parser from 'react-html-parser'
import apis from '../../../modules/apis'
import { axiosPost } from '../../../modules/axiosHelper'
import { useGoToPreviousPage } from '../../../modules/hooks/common/useGoToPreviousPage'
import { n_classroom, s_common, s_preparation } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { RINGLE_LOGO_ROUND } from '../../../modules/images'
import urls from '../../../modules/urls'
import NoSSR from '../../common/NoSSR'
import CreditBadge from '../../common/buttons/CreditBadge'
import GoogleTranslate from '../GoogleTranslate'
import ZoomGuideModalBody from '../ZoomGuideModalBody'

export const CONTENT_NAV_BAR_HEIGHT = 70
interface ContentNavBarType {
  onExit?: any
  noExit?: boolean
  onExitContent?: any
  showGoogleTranslate?: boolean
  setShowGoogleTranslate?: any
  setShowZoomGuide?: any
  showZoomGuide?: any
  showChatBox?: any
  setShowChatBox?: any
  chatList?: any
  lesson?: any
  pageType?: string | ReactElement
  description?: any
  isLesson?: boolean
  isPrestudy?: boolean
  isLessonPast?: boolean
  button1?: any
  button2?: any
  button3?: any
  button4?: any
  clx?: string
  location?: any
  isPast?: boolean
  noTopSideTab?: boolean
  homeLink?: any
  TutorChatBox?: any
  exitLabel?: string
  handleLessonInfo?: any
}
const ContentNavBar: React.FC<ContentNavBarType> = ({
  onExit,
  noExit = false,
  onExitContent,
  exitLabel,
  showGoogleTranslate,
  setShowGoogleTranslate,
  setShowZoomGuide,
  showZoomGuide,
  showChatBox,
  chatList,
  setShowChatBox,
  lesson,
  pageType,
  description,
  isLesson,
  isPrestudy,
  isLessonPast,
  button1,
  button2,
  button3,
  button4,
  clx,
  location,
  noTopSideTab,
  homeLink = urls.student.newPortal.home,
  TutorChatBox,
  handleLessonInfo,
}) => {
  const { t } = useTranslation()
  const toPreviousPage = useGoToPreviousPage(location, isLessonPast)

  const handleExitClick = (e) => {
    e.preventDefault()
    if (onExitContent) {
      onExitContent()
      return
    }
    if (onExit) {
      onExit(e)
    } else {
      toPreviousPage()
    }
  }
  return (
    <Fragment>
      {showGoogleTranslate && <GoogleTranslate lessonId={lesson?.id} onClose={() => setShowGoogleTranslate(false)} />}
      {showZoomGuide && (
        <ZoomGuideModalBody
          showPopup={showZoomGuide}
          setShowPopup={setShowZoomGuide}
          lesson={lesson}
          onClose={() => setShowZoomGuide(false)}
          setShowChatBox={setShowChatBox}
        />
      )}
      {showChatBox && !TutorChatBox && (
        <ChatBox lesson={lesson} showChatBox={showChatBox} chatList={chatList} onClose={() => setShowChatBox(false)} />
      )}
      {TutorChatBox}
      <div className="fixed top-[0px] left-[0px] z-[60] h-[70px] w-full px-[32px] sm:w-full">
        <div className="flex h-[70px] flex-row items-center justify-between">
          {noExit || (
            <div>
              <div onClick={(e) => handleExitClick(e)} className="flex cursor-pointer flex-row">
                <div className="Body2-14Bd flex items-center text-gray-700">
                  <QIcon_chevron_bold_left width={16} height={16} />
                  {exitLabel ? exitLabel : t(s_preparation.exit)}
                </div>
              </div>
            </div>
          )}
          <div className={classNames('auto flex items-center gap-x-[8px] whitespace-nowrap', clx)}>
            <div className="text-center">
              <div
                className="flex h-[42px] cursor-pointer items-center justify-start rounded-[5px] border-[1.5px] border-gray-300 py-[10px] px-[12px]"
                onClick={handleLessonInfo}
              >
                {(isLesson || isPrestudy) && (
                  <CreditBadge
                    duration={lesson?.duration}
                    style={{
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      color: '#140F33',
                      backgroundColor: 'rgb(228, 231, 244)',
                      fontWeight: 'bold',
                    }}
                  />
                )}
                <div className="Body2-14Md mdd:hidden mr-[8px] ml-[8px] whitespace-nowrap">{description}</div>
              </div>
            </div>
            {button1 && <div>{button1}</div>}
            {button2 && <div>{button2}</div>}
            {button3 && <div>{button3}</div>}
            {button4 && <div>{button4}</div>}
            <div className="mr-[16px]" />
          </div>
        </div>
      </div>
    </Fragment>
  )
}

export default ContentNavBar

type ChatBoxPropsType = {
  lesson: any
  chatList: any
  showChatBox: any
  onClose: any
}

const ChatBox = ({ lesson, chatList, showChatBox, onClose }: ChatBoxPropsType) => {
  const { t } = useTranslation()
  const chatRef = useRef(null)
  const inputRef = useRef(null)
  const endOfDivRef = useRef(null)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const scrollToEnd = () => {
    endOfDivRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (showChatBox) {
      // inputRef.current.focus()
      window.scrollTo(0, 0)
    }
  }, [showChatBox])

  useEffect(() => {
    if (chatList.length > 0) {
      scrollToEnd()
    }
  }, [chatList])

  const handleStudentChat = async (e, text, type) => {
    e.preventDefault()
    if (text.length > 0) {
      setLoading(true)
      const response = await axiosPost(apis.lessons.chat(), {
        lesson_id: lesson.id,
        user_id: lesson.student_id,
        text: text,
        type: type,
      })
      setLoading(false)
      if (response.success) {
        if (message) {
          setMessage('')
        }
        scrollToEnd()
      }
    }
  }

  // TUTOR
  const today = new Date()
  const [recordTime, setRecordTime] = useState(today.getHours() + ':' + today.getMinutes() + ':' + today.getSeconds())

  return (
    <Draggable bounds=".body" handle=".handle">
      <div
        style={{ zIndex: 1070 }}
        className="border-1 absolute top-[64px] right-[10px] w-[480px] rounded-[5px] border-gray-100 bg-white shadow-md"
      >
        <div className="handle cursor-move border-b border-gray-200 p-[20px]">
          <div className="flex items-center justify-between py-[3px]">
            <div className="Title1-20Bd">{t(n_classroom.popup_help_chat_title)}</div>
            <div onClick={onClose} className="cursor-pointer">
              <QIcon_close_bold width={20} height={20} />
            </div>
          </div>
        </div>
        <div ref={chatRef} className="max-h-[500px] min-h-[200px] overflow-y-auto bg-gray-50 px-[16px] py-[16px]">
          <div className="mb-[4px] flex max-w-[400px] justify-start">
            <div className="msg_container ml-[2px]">
              <RingleAvatar />
              <div className="mt-[12px] flex flex-wrap justify-end gap-[6px]">
                <div>
                  <DSButton
                    type={BUTTON_TYPE.OUTLINE}
                    size={BUTTON_SIZE.SM}
                    onClick={(e) => handleStudentChat(e, t(n_classroom.help_chat_audio_problem), 'sound')}
                  >
                    <DSButton.Label>{t(n_classroom.help_chat_audio_problem)}</DSButton.Label>
                  </DSButton>
                </div>

                <div>
                  <DSButton
                    type={BUTTON_TYPE.OUTLINE}
                    size={BUTTON_SIZE.SM}
                    onClick={(e) => handleStudentChat(e, t(n_classroom.help_chat_video_problem), 'video')}
                  >
                    <DSButton.Label>{t(n_classroom.help_chat_video_problem)}</DSButton.Label>
                  </DSButton>
                </div>
                <div>
                  <DSButton
                    type={BUTTON_TYPE.OUTLINE}
                    size={BUTTON_SIZE.SM}
                    onClick={(e) => handleStudentChat(e, t(n_classroom.help_chat_note_problem), 'google_docs')}
                  >
                    <DSButton.Label>{t(n_classroom.help_chat_note_problem)}</DSButton.Label>
                  </DSButton>
                </div>
                <div>
                  <DSButton
                    type={BUTTON_TYPE.OUTLINE}
                    size={BUTTON_SIZE.SM}
                    onClick={(e) => handleStudentChat(e, t(n_classroom.help_chat_zoom_problem), 'zoom')}
                  >
                    <DSButton.Label>{t(n_classroom.help_chat_zoom_problem)}</DSButton.Label>
                  </DSButton>
                </div>
              </div>
            </div>
          </div>
          {chatList.map((item, i) =>
            item.user_id == 1 ? (
              <div key={i} className="msg_container animated fadeIn mr-auto mb-[3px]">
                <RingleAvatar />
                <div className="text-h5 mt-[2px] mr-auto max-w-[330px] rounded-[4px] border border-gray-200 bg-white py-[8px] px-[12px] text-left font-normal text-black">
                  <NoSSR>{item.content && Parser(item.content)}</NoSSR>
                </div>
              </div>
            ) : (
              <div key={i} className="msg_container animated fadeIn mb-[3px] text-right">
                <div className="text-right text-[12px] text-gray-300">{item.date}</div>
                <div className="text-h5 mt-[2px] ml-auto inline-block max-w-[330px] rounded-[4px] border border-gray-200 bg-white py-[8px] px-[12px] text-right font-normal text-black">
                  <NoSSR>{item.content && Parser(item.content)}</NoSSR>
                </div>
              </div>
            )
          )}
          <div ref={endOfDivRef} />
        </div>
        {/* <div className="px-[16px] py-[16px]">
          <form onSubmit={(e) => handleStudentChat(e, message, null)}>
            <div className="flex items-center justify-center gap-x-[8px]">
              <div className="w-full">
                <input
                  type="text"
                  className="w-full border-gray-200 bg-gray-50"
                  style={{ borderRadius: 4, height: 52 }}
                  id="chat-message"
                  ref={inputRef}
                  placeholder={t(s_classroom.write_your_message)}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              <div className="ml-auto w-[92px]">
                <DSButton
                  className="whitespace-nowrap"
                  isLoading={loading}
                  size={BUTTON_SIZE.MD}
                  type={BUTTON_TYPE.SOLID}
                  onClick={(e) => handleStudentChat(e, message, null)}
                >
                  <DSButton.Label>{t(s_common.send)}</DSButton.Label>
                </DSButton>
              </div>
            </div>
          </form>
        </div> */}
      </div>
    </Draggable>
  )
}

export const RingleAvatar = () => {
  const { t } = useTranslation()
  return (
    <div className="mb-[4px] flex">
      <img src={RINGLE_LOGO_ROUND} className="user_img_msg mr-[4px] w-[20px] rounded-full" />
      {t(s_common.ringle)}
    </div>
  )
}
